import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiClient from '../../../services/apiClient';
import { loadPersistedWidgets,saveWidgets,clearWidgets,shouldSyncWithBackend,getUserId,isAuthenticated 
} from '../../../utils/widgetPersistence';

export const fetchAdminDashboardStats = createAsyncThunk(
  'admin/fetchDashboardStats',
  async ({ timePeriod, functions }, { rejectWithValue }) => {
    try {
      const params = {timePeriod,functions: functions.join(',')};
      const response = await apiClient.get(`/admin/dashboard`, {params});
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const fetchAllUsers = createAsyncThunk(
  'admin/fetchAllUsers',
async (
    { page = 1, limit = 10, searchTerm = '', status = '', role = '', sortBy = 'createdAt', sortOrder = 'desc' } = {},
    { rejectWithValue }
  ) => {
    try {
      const params = { page, limit, searchTerm, status, role, sortBy, sortOrder };

      const response = await apiClient.get('/admin/users',{params});
      return {
        users: response.data.data.users,
        pagination: response.data.data.pagination
      };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);
 
export const removeUser = createAsyncThunk(
  'admin/removeUser',
  async (id, { rejectWithValue }) => {
    try {
      await apiClient.delete(`/admin/users/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const updateUserStatus = createAsyncThunk(
  'admin/updateUserStatus',
  async ({ id, status, reason }, { rejectWithValue }) => {
    try {
      const response = await apiClient.patch(
        `/admin/users/${id}/status`,{ status, reason });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const fetchUserSubscriptions = createAsyncThunk(
  'admin/fetchUserSubscriptions',
  async (
    { page = 1, limit = 10, searchTerm = '', plan = '', status = '', sortBy = 'subscriptionDate', sortOrder = 'desc' } = {},
    { rejectWithValue }
  ) => {
    try {
      const params = { page, limit, searchTerm, plan, status, sortBy, sortOrder };
      const response = await apiClient.get('/admin/subscriptions',{params});
      return {
        subscriptions: response.data.data.subscriptions,
        pagination: response.data.data.pagination,
      };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const fetchAllPlans = createAsyncThunk(
  'admin/fetchAllPlans',
  async (_,{ rejectWithValue }) => {
    try {
      const response = await apiClient.get('/admin/plans');
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const createPlan = createAsyncThunk(
  'admin/createPlan',
  async (planData, { rejectWithValue }) => {
    try {
      const response = await apiClient.post('/admin/plans', planData);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const updatePlan = createAsyncThunk(
  'admin/updatePlan',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await apiClient.patch(`/admin/plans/${id}`, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const deletePlan = createAsyncThunk(
  'admin/deletePlan',
  async (id, { rejectWithValue }) => {
    try {
      await apiClient.delete(`/admin/plans/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const fetchAnalyticsData = createAsyncThunk(
  'admin/fetchAnalyticsData',
  async ({ type, period }, { rejectWithValue }) => {
    try {
      const response = await apiClient.get(`/admin/analytics/${type}`, {params: { period }});
      return {
        ...response.data,
        type 
      };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const processCashbotQuery = createAsyncThunk(
  'admin/processCashbotQuery',
  async (query, { rejectWithValue }) => {
    try {
      const response = await apiClient.post(`/admin/dashboard/cashbot/query`, {query});
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message || error);
    }
  }
);

export const createWidgetFromCashbot = createAsyncThunk(
  'admin/createWidgetFromCashbot',
  async ({ query, widgetConfig }, { rejectWithValue }) => {
    try {
       const response = await apiClient.post(`/admin/dashboard/cashbot/create-widget`, 
            {  query: query,
              widgetConfig: {
                ...widgetConfig,
                data: widgetConfig.data,
                visualization: widgetConfig?.visualization
              }
            }
          );
        return response.data
    } catch (error) {
      return rejectWithValue(error || error.message || 'Failed to create widget');
    }
  }
);

// Fetch widgets from database
export const fetchWidgets = createAsyncThunk(
  'admin/fetchWidgets',
  async ({ forceRefresh = false } = {}, { rejectWithValue }) => {
    try {
      const userId = getUserId();
      if (!isAuthenticated()) {
        return loadPersistedWidgets(userId);
      }
      // Check if we should sync with backend
      if (!forceRefresh && !shouldSyncWithBackend()) {
        return loadPersistedWidgets(userId);
      }
      const authData = JSON.parse(localStorage.getItem('authData') || '{}');
      const token = authData?.accessToken;
      if (!token) {
        return loadPersistedWidgets(userId);
      }

      try {
        const response = await apiClient.get(`/admin/dashboard/widgets`);
        const transformedWidgets = response.data.map(widget => ({
          id: widget._id, 
          title: widget.title,
          type: widget.type,
          query: widget.config?.query || widget.query || '',
          data: widget.data || {},
          config: {
            ...widget.config,
            chartType: widget.config?.chartType || widget.type,
            title: widget.title,
            type: widget.type,
          },
          createdAt: widget.createdAt,
          updatedAt: widget.updatedAt,
          userId: widget.userId,
          contextType: widget.contextType,
          position: widget.position,
          updateInterval: widget.config?.updateInterval || 3600000,
        }));
        
        saveWidgets(transformedWidgets, userId);
        return transformedWidgets;
      } catch (error) {
        if (error.response?.status === 404) {
          return loadPersistedWidgets(userId);
        }
        throw error;
      }
    } catch (error) {
      console.error('Error fetching widgets:', error);
      return loadPersistedWidgets(getUserId());
    }
  }
);

// Delete widget from database
export const deleteWidget = createAsyncThunk(
  'admin/deleteWidget',
  async (widgetId, { rejectWithValue }) => {
    try {
      const userId = getUserId();
       const response = await apiClient.delete(`/admin/dashboard/widgets/${widgetId}`);
      // Remove from localStorage
      const persistedWidgets = loadPersistedWidgets(userId);
      const updatedWidgets = persistedWidgets.filter(widget => widget.id !== widgetId);
      saveWidgets(updatedWidgets, userId);
      
      return widgetId;
    } catch (error) {
      console.error('Error deleting widget:', error);
      return rejectWithValue(error.message || 'Failed to delete widget');
    }
  }
);

// Update widget data (for hourly updates)
export const refreshWidgetData = createAsyncThunk(
  'admin/refreshWidgetData',
  async (widgetId, { rejectWithValue }) => {
    try {
      const response = await apiClient.get(`/admin/dashboard/widgets/${widgetId}/refresh`);
        return { widgetId, data: response.data };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to refresh widget data');
    }
  }
);

export const syncWidgetsWithBackend = createAsyncThunk(
  'admin/syncWidgetsWithBackend',
  async (_, { getState }) => {
    try {
      if (!isAuthenticated()) {
        return { synced: false, reason: 'Not authenticated' };
      }

      const userId = getUserId();
      const state = getState();
      const localWidgets = state.admin.widgets;

      try {
        // Sync local widgets to backend
        const response = await apiClient.post(`/admin/dashboard/widgets/sync`, 
          { widgets: localWidgets, userId });
        return { synced: true, widgets: response.data };
      } catch (error) {
        if (error.response?.status === 404) {
          return { synced: false, reason: 'Sync endpoint not available' };
        }
        throw error;
      }
    } catch (error) {
      console.error('Error syncing widgets:', error);
      return { synced: false, reason: error.message };
    }
  }
);

export const updateWidgetTitle = createAsyncThunk(
  'admin/updateWidgetTitle',
  async ({ id, updateData }, { rejectWithValue }) => {
    try {
      const response = await apiClient.put(`/admin/dashboard/widgets/${id}`, updateData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Thunk to refresh widget data
export const refreshWidget = createAsyncThunk(
  'admin/refreshWidget',
  async (widgetId, { rejectWithValue }) => {
    try {
      const response = await apiClient.post(`/admin/dashboard/widgets/${widgetId}/refresh`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

// Thunk to save widget layout
export const saveWidgetLayout = createAsyncThunk(
  'admin/saveWidgetLayout',
  async ({ layoutMap, contextType = 'admin', canvasId = null }, { rejectWithValue }) => {
    try {
      const response = await apiClient.patch('admin/dashboard/widgets/layout/save', {
        layoutMap,
        contextType,
        canvasId
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to save widget layout'
      );
    }
  }
);

// Thunk to update multiple widget positions
export const updateMultipleWidgetPositions = createAsyncThunk(
  'admin/updateMultipleWidgetPositions',
  async ({ layoutMap, contextType = 'admin' }, { rejectWithValue }) => {
    try {
      const response = await apiClient.patch('admin/dashboard/widgets/positions/bulk', {
        layoutMap,
        contextType,
      })
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to update widget positions'
      );
    }
  }
);


export const fetchAllTutorials = createAsyncThunk(
  'admin/fetchAllTutorials',
  async (
    { 
      page = 1, 
      limit = 10, 
      searchTerm = '', 
      category = '', 
      level = '', 
      isActive = null, 
      sortBy = 'sequence', 
      sortOrder = 'asc' 
    } = {},
    { rejectWithValue }
  ) => {
    try {
      const params = { page, limit, searchTerm, category, level, sortBy, sortOrder };
      if (isActive !== null) params.isActive = isActive;
      
      const response = await apiClient.get('/admin/tutorials', { params });
      return {
        tutorials: response.data.data.tutorials,
        pagination: response.data.data.pagination
      };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const getTutorialById = createAsyncThunk(
  'admin/getTutorialById',
  async (id, { rejectWithValue }) => {
    try {
      const response = await apiClient.get(`/admin/tutorials/${id}`);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const createTutorial = createAsyncThunk(
  'admin/createTutorial',
  async (tutorialData, { rejectWithValue }) => {
    try {
      const response = await apiClient.post('/admin/tutorials', tutorialData);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const updateTutorial = createAsyncThunk(
  'admin/updateTutorial',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await apiClient.patch(`/admin/tutorials/${id}`, data);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const removeTutorial = createAsyncThunk(
  'admin/removeTutorial',
  async (id, { rejectWithValue }) => {
    try {
      await apiClient.delete(`/admin/tutorials/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const reorderTutorial = createAsyncThunk(
  'admin/reorderTutorial',
  async ({ id, newSequence }, { rejectWithValue }) => {
    try {
      const response = await apiClient.patch(`/admin/tutorials/${id}/reorder`, { newSequence });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

export const toggleTutorialActive = createAsyncThunk(
  'admin/toggleTutorialActive',
  async (id, { rejectWithValue }) => {
    try {
      const response = await apiClient.patch(`/admin/tutorials/${id}/toggle-active`);
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message);
    }
  }
);

const initialState = {
  dashboardStats: null,
  tutorials: {
  tutorials: [],
    pagination: {
      limit: 10,
      page: 1,
      pages: 1,
      total: 0
    }
  },
  users: {
    users: [], 
    pagination: {
      limit: 10,
      page: 1,
      pages: 1,
      total: 0
    }
  },
  subscriptions: [],
  plans: [],
  loading: false,
  analyticsLoading: false,
  cashbotLoading: false,   
  error: null,
  analyticsData: null,
  widgets: loadPersistedWidgets(getUserId()), 
  lastSyncTime: null, 
  cashbot: {
    isOpen: false,
    messages: [],
    isTyping: false,
    lastQueryResult: null,
  },
  layoutSaving: false,
  layoutError: null,
  lastLayoutSave: null,
};

// Update the adminSlice to persist widgets
const adminSlice = createSlice({
  name: 'admin',
  initialState,
  reducers: {
    clearAnalyticsData: (state) => {
      state.analyticsData = null;
    },
    toggleCashbot: (state) => {
      state.cashbot.isOpen = !state.cashbot.isOpen;
    },
    closeCashbot: (state) => {
      state.cashbot.isOpen = false;
    },
    addCashbotMessage: (state, action) => {
      state.cashbot.messages.push(action.payload);
    },
    setCashbotTyping: (state, action) => {
      state.cashbot.isTyping = action.payload;
    },
    clearCashbotMessages: (state) => {
      state.cashbot.messages = [];
    },
    loadCashbotHistory: (state, action) => {
      state.cashbot.messages = action.payload;
    },
    addWidget: (state, action) => {
      state.widgets.push(action.payload);
      saveWidgets(state.widgets, getUserId());
    },
    
    removeWidget: (state, action) => {
      state.widgets = state.widgets.filter(widget => widget.id !== action.payload);
      saveWidgets(state.widgets, getUserId());
    },
    
    updateWidget: (state, action) => {
      const index = state.widgets.findIndex(widget => widget.id === action.payload.id);
      if (index !== -1) {
        state.widgets[index] = { ...state.widgets[index], ...action.payload };
        saveWidgets(state.widgets, getUserId());
      }
    },
    
    clearAllWidgets: (state) => {
      state.widgets = [];
      clearWidgets(getUserId());
    },
    
    handleUserLogout: (state) => {
      state.widgets = [];
      state.lastSyncTime = null;
    },
    
    handleUserLogin: (state) => {
      // Load user-specific widgets
      state.widgets = loadPersistedWidgets(getUserId());
    },
    
    loadWidgets: (state, action) => {
      state.widgets = action.payload || [];
    },
    clearLayoutError: (state) => {
      state.layoutError = null;
    },
  },
  
  extraReducers: (builder) => {
    builder
      .addCase(fetchAdminDashboardStats.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAdminDashboardStats.fulfilled, (state, action) => {
        state.loading = false;
        state.dashboardStats = action.payload;
        state.error = null;
      })
      .addCase(fetchAdminDashboardStats.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchAllUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAllUsers.fulfilled, (state, action) => {
        state.loading = false;
        state.users = action.payload;
        state.error = null;
      })
      .addCase(fetchAllUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(removeUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(removeUser.fulfilled, (state, action) => {
        state.loading = false;
        state.users.users = state.users.users.filter(user => user._id !== action.payload);
        state.users.pagination.total -= 1;
        state.error = null;
      })
      .addCase(removeUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateUserStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateUserStatus.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.users.users.findIndex(user => user._id === action.payload.id);
        if (index !== -1) {
          state.users.users[index].status = action.payload.status;
          state.users.users[index].statusReason = action.payload.reason;
        }
        state.error = null;
      })
      .addCase(updateUserStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchUserSubscriptions.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchUserSubscriptions.fulfilled, (state, action) => {
        state.loading = false;
        state.subscriptions = action.payload;
      })
      .addCase(fetchUserSubscriptions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchAllPlans.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchAllPlans.fulfilled, (state, action) => {
        state.loading = false;
        state.plans = action.payload;
      })
      .addCase(fetchAllPlans.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(createPlan.fulfilled, (state, action) => {
        state.plans.push(action.payload);
      })
      .addCase(updatePlan.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updatePlan.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.plans.findIndex(plan => plan._id === action.payload._id);
        if (index !== -1) {
          state.plans[index] = action.payload;
        }
        state.error = null;
      })
      .addCase(updatePlan.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(deletePlan.fulfilled, (state, action) => {
        state.plans = state.plans.filter(plan => plan._id !== action.payload);
      })
      .addCase(fetchAnalyticsData.pending, (state) => {
        state.analyticsLoading = true; 
        state.analyticsData = null; 
      })
      .addCase(fetchAnalyticsData.fulfilled, (state, action) => {
        state.analyticsLoading = false;
        state.analyticsData = action.payload;
      })
      .addCase(fetchAnalyticsData.rejected, (state, action) => {
        state.analyticsLoading = false;
        state.error = action.payload;
      })
      .addCase(processCashbotQuery.pending, (state) => {
        state.cashbotLoading = true;
        state.cashbot.isTyping = true;
        state.error = null;
      })
      .addCase(processCashbotQuery.fulfilled, (state, action) => {
        state.cashbotLoading = false; 
        state.cashbot.isTyping = false;
        
        // Handle the new response structure
        const response = action.payload;
        
        // Use the actual response from the API/mock
        const botMessage = {
          id: Date.now(),
          type: 'bot',
          content: response.answer || response.response || 'I processed your query successfully.',
          timestamp: new Date().toISOString(),
          data: response.data,
          canCreateWidget: response.canCreateWidget,
          widgetType: response.widgetType,
          widgetConfig: response.widgetConfig,
          query: action.meta.arg,
        };
        
        state.cashbot.messages.push(botMessage);
        state.cashbot.lastQueryResult = response;
      })
      .addCase(processCashbotQuery.rejected, (state, action) => {
        state.cashbotLoading = false; 
        state.cashbot.isTyping = false;
        state.error = action.payload;
      })
      .addCase(createWidgetFromCashbot.pending, (state) => {
        state.cashbotLoading = true;
        state.error = null;
      })
      .addCase(createWidgetFromCashbot.fulfilled, (state, action) => {
        state.cashbotLoading = false;
        
        if (action.payload) {
          const newWidget = {
            id: action.payload._id || `widget_${Date.now()}`,
            title: action.payload.title || 'Untitled Widget',
            type: action.payload.type || 'metric',
            query: action.payload.query || '',
            data: action.payload.data || {},
            config: action.payload.config || {},
            createdAt: action.payload.createdAt || new Date().toISOString(),
            contextType: action.payload.contextType || 'admin',
            userId: action.payload.userId || getUserId(),
            position: action.payload.position || { x: 0, y: 0, w: 6, h: 4 },
            updateInterval: action.payload.updateInterval || 3600000,
          };
          
          state.widgets.push(newWidget);
          

          
          // Use enhanced persistence
          saveWidgets(state.widgets, getUserId());
          
          const successMessage = {
            id: Date.now() + 1,
            type: 'bot',
            content: `Widget "${newWidget.title}" has been created successfully and added to your analytics dashboard!`,
            timestamp: new Date().toISOString(),
            isSuccess: true,
          };
          state.cashbot.messages.push(successMessage);
        }
      })
      .addCase(createWidgetFromCashbot.rejected, (state, action) => {
        state.cashbotLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchWidgets.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchWidgets.fulfilled, (state, action) => {
        state.loading = false;
        
        let widgets = action.payload || [];
        widgets = widgets.map(widget => ({
          id: widget.id || widget._id, 
          title: widget.title,
          type: widget.type,
          query: widget.query,
          data: widget.data || {},
          config: widget.config || {},
          createdAt: widget.createdAt,
          updatedAt: widget.updatedAt,
          userId: widget.userId,
          contextType: widget.contextType,
          position: widget.position,
          updateInterval: widget.updateInterval || 3600000,
        }));
        
        state.widgets = widgets;
        state.lastSyncTime = new Date().toISOString();
        
        saveWidgets(widgets, getUserId());
      })
      .addCase(fetchWidgets.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(deleteWidget.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteWidget.fulfilled, (state, action) => {
        state.loading = false;
        state.widgets = state.widgets.filter(widget => widget.id !== action.payload);
        saveWidgets(state.widgets, getUserId());
      })
      .addCase(deleteWidget.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(refreshWidgetData.pending, (state) => {
        state.loading = true;
      })
      .addCase(refreshWidgetData.fulfilled, (state, action) => {
        const { widgetId, data } = action.payload;
        const widgetIndex = state.widgets.findIndex(widget => widget.id === widgetId);
        if (widgetIndex !== -1) {
          state.widgets[widgetIndex].data = data;
          state.widgets[widgetIndex].lastUpdated = new Date().toISOString();
          localStorage.setItem('admin_widgets', JSON.stringify(state.widgets));
        }
      })
      .addCase(refreshWidgetData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(syncWidgetsWithBackend.pending, (state) => {
        state.loading = true;
      })
      .addCase(syncWidgetsWithBackend.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.synced) {
          state.widgets = action.payload.widgets;
          state.lastSyncTime = new Date().toISOString();
          saveWidgets(state.widgets, getUserId());
        } else {
        }
      })
      .addCase(syncWidgetsWithBackend.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Save widget layout
      .addCase(saveWidgetLayout.pending, (state) => {
        state.layoutSaving = true;
        state.layoutError = null;
      })
      .addCase(saveWidgetLayout.fulfilled, (state, action) => {
        state.layoutSaving = false;
        state.lastLayoutSave = new Date().toISOString();

        const { layoutMap } = action.meta.arg || {};
        if (layoutMap) {
          state.widgets = state.widgets.map(w => {
            const pos = layoutMap[w.id] || layoutMap[w._id];
            if (pos) {
              return {
                ...w,
                position: {
                  ...(w.position || {}),
                  ...pos
                }
              };
            }
            return w;
          });
          // Persist to localStorage
          saveWidgets(state.widgets, getUserId());
        }
      })
      .addCase(saveWidgetLayout.rejected, (state, action) => {
        state.layoutSaving = false;
        state.layoutError = action.payload;
      })
      // Update multiple positions
      .addCase(updateMultipleWidgetPositions.pending, (state) => {
        state.layoutSaving = true;
      })
      .addCase(updateMultipleWidgetPositions.fulfilled, (state, action) => {
        state.layoutSaving = false;
        // Update local widget positions if widgets are returned
        if (action.payload.widgets) {
          action.payload.widgets.forEach(updatedWidget => {
            const index = state.widgets.findIndex(w => w.id === updatedWidget.id || w._id === updatedWidget._id);
            if (index !== -1) {
              state.widgets[index].position = updatedWidget.position;
            }
          });
        }
        saveWidgets(state.widgets, getUserId());
      })
      .addCase(updateMultipleWidgetPositions.rejected, (state, action) => {
        state.layoutSaving = false;
        state.layoutError = action.payload;
      })
      // Update widget
    .addCase(updateWidgetTitle.fulfilled, (state, action) => {
      if (state.widgets) {
        const index = state.widgets.findIndex(w => w.id === action.payload.id || w._id === action.payload._id);
        if (index !== -1) {
          state.widgets[index] = { ...state.widgets[index], ...action.payload };
        }
      }
      state.loading = false;
    })
    .addCase(updateWidgetTitle.pending, (state) => {
      state.loading = true;
    })
    .addCase(updateWidgetTitle.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload || 'Failed to update widget';
    })

    // Refresh widget
    .addCase(refreshWidget.fulfilled, (state, action) => {
      if (state.widgets) {
        const index = state.widgets.findIndex(w => w.id === action.payload.id || w._id === action.payload._id);
        if (index !== -1) {
          state.widgets[index] = action.payload;
        }
      }
      state.loading = false;
    })
    .addCase(refreshWidget.pending, (state) => {
      state.loading = true;
    })
    .addCase(refreshWidget.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload || 'Failed to refresh widget';
    })
    .addCase(fetchAllTutorials.pending, (state) => {
  state.loading = true;
  state.error = null;
})
.addCase(fetchAllTutorials.fulfilled, (state, action) => {
  state.loading = false;
  state.tutorials = action.payload;
})
.addCase(fetchAllTutorials.rejected, (state, action) => {
  state.loading = false;
  state.error = action.payload;
})
.addCase(getTutorialById.pending, (state) => {
  state.loading = true;
})
.addCase(getTutorialById.fulfilled, (state, action) => {
  state.loading = false;
})
.addCase(getTutorialById.rejected, (state, action) => {
  state.loading = false;
  state.error = action.payload;
})
.addCase(createTutorial.fulfilled, (state, action) => {
  state.tutorials.tutorials.push(action.payload);
})
.addCase(updateTutorial.fulfilled, (state, action) => {
  const index = state.tutorials.tutorials.findIndex(t => t.id === action.payload.id);
  if (index !== -1) {
    state.tutorials.tutorials[index] = action.payload;
  }
})
.addCase(removeTutorial.fulfilled, (state, action) => {
  state.tutorials.tutorials = state.tutorials.tutorials.filter(t => t.id !== action.payload);
})
.addCase(reorderTutorial.fulfilled, (state, action) => {
  const index = state.tutorials.tutorials.findIndex(t => t.id === action.payload.id);
  if (index !== -1) {
    state.tutorials.tutorials[index] = action.payload;
  }
})
.addCase(toggleTutorialActive.fulfilled, (state, action) => {
  const index = state.tutorials.tutorials.findIndex(t => t.id === action.payload.id);
  if (index !== -1) {
    state.tutorials.tutorials[index] = action.payload;
  }
})
  },
});

export const { 
  clearAnalyticsData, 
  toggleCashbot, 
  closeCashbot, 
  addCashbotMessage, 
  setCashbotTyping, 
  clearCashbotMessages, 
  loadCashbotHistory, 
  addWidget, 
  removeWidget, 
  updateWidget,
  loadWidgets,
  clearAllWidgets,   
  handleUserLogout,
  handleUserLogin,   
} = adminSlice.actions;

export const selectAdminDashboardStats = (state) => state.admin.dashboardStats;
export const selectUsers = (state) => state.admin.users;
export const selectLoading = (state) => state.admin.loading;
export const selectAdminLoading = (state) => state.admin.loading;
export const selectError = (state) => state.admin.error;
export const selectUserSubscriptions = (state) => state.admin.subscriptions;
export const selectPlans = (state) => state.admin.plans;
export const selectAnalyticsLoading = (state) => state.admin.analyticsLoading;
export const selectCashbotLoading = (state) => state.admin.cashbotLoading;
export const selectTutorials = (state) => state.admin.tutorials;

export default adminSlice.reducer;